import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { flatten, pathOr } from 'ramda';
import { useApolloClient, useMutation } from '@apollo/client';

import Spinner from 'admin-components/components/Spinner';
import SeatSelection from 'components/shared/SeatSelection';
import { Row, Page, Column } from 'components/shared/Layout';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import PassengerDetailsForm from 'pages/BookingPathPassengerDetails/PassengerDetailsForm';
import GroupDetails from 'pages/GroupDetails/GroupDetails.js';
import { ConfirmButton } from 'components/ui/Button';
import formatPassengers from 'utils/formatPassengers';
import { useDefaultedCurrencyFromURL } from 'utils/hooks/currency';
import {
  ADD_ADDITIONAL_DETAILS,
  ADD_NOTE_TO_BOOKING,
  CONFIRM_BOOKING,
  CONFIRM_FREE_UPGRADE,
  REVERT_CHANGE,
  UPDATE_PASSENGERS
} from 'store/mutations';
import { DEFAULT_LANGUAGE, LANGUAGE_KEY } from 'constants/booking';
import { correctlyFormatDate } from 'utils/dates';
import { hasHotel, nonCancelledEurostarAndGreenspeedLegs, nonCancelledLegs } from 'utils/booking';

import { BackButton } from '../ExchangeDetails/ExchangeDetails.styles';
import { ButtonRow } from './ExchangeSeatSelection.styles';
import { CONFIRM_TIER_BENEFIT_UPGRADE, GET_MEMBERSHIP_DETAILS, REDEEM_BUSINESS_ACCOUNT_BENEFIT } from '../../../store/queries';
import { LOYALTY_NUMBER_PREFIX } from '../../../constants/passengers';
import { parsePhoneNumber } from '../../../utils/parsePhoneNumber';
import { renameKeys } from '../../../utils/functions';
import discountCardsMap from '../../../types/discountCardsMap';
import { getLoyaltyData } from '../../../utils/loyaltyData';
import updateBookingLanguage from 'utils/updateBookingLanguage';
import RevertExchangeButton from '../../../components/shared/RevertExchangeButton';
import { bookingStatus } from '../../../types/booking';
import { DIRECTIONS } from '../../../types/classes';
import { types } from '../../../types/passengers';
import { useMeals } from 'utils/hooks/useMeals';
import { isNoMealRoute } from 'utils/meals';
import { hasGroupDetailsForm } from 'utils/isGroupBooking';
import AgentPortalError from 'errors/AgentPortalError';

const ExchangeSeatSelection = ({ booking, history, user }) => {
  const apolloClient = useApolloClient();
  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');
  const isFreeUpgrade = new URLSearchParams(history?.location?.search).get('isFreeUpgrade');
  const isTierBenefitUpgrade = new URLSearchParams(history?.location?.search).get('isTierBenefitUpgrade');
  const isBusinessBenefitUpgrade = new URLSearchParams(history?.location?.search).get('isBusinessBenefitUpgrade');
  const businessBenefitName = new URLSearchParams(history?.location?.search).get('businessBenefitName');
  const businessId = new URLSearchParams(history?.location?.search).get('businessId');
  const addPassengers = new URLSearchParams(history?.location?.search).get('addPassengers');
  const waivePoints = new URLSearchParams(history?.location?.search).get('waivePoints');
  const inboundClass = new URLSearchParams(history?.location?.search).get('InClass');
  const outboundClass = new URLSearchParams(history?.location?.search).get('OutClass');
  const [currency] = useDefaultedCurrencyFromURL({
    availableCurrencies: user?.paymentCurrencies || [],
    defaultCurrency: booking.currency,
    isMultiCurrency: booking.isMultiCurrency
  });

  const [updatePassengers, { loading: updatePassengersLoading }] = useMutation(UPDATE_PASSENGERS);
  const [confirmTierBenefitUpgrade, { loading: confirmTierBenefitUpgradeLoading }] = useMutation(CONFIRM_TIER_BENEFIT_UPGRADE);
  const [confirmFreeUpgrade, { loading: confirmFreeUpgradeLoading }] = useMutation(CONFIRM_FREE_UPGRADE);
  const [redeemBusinessAccountBenefit, { loading: redeemBusinessBenefitLoading }] = useMutation(REDEEM_BUSINESS_ACCOUNT_BENEFIT);
  const [confirmBooking, { loading: confirmBookingLoading }] = useMutation(CONFIRM_BOOKING);
  const [addNoteToBooking, { loading: addNoteToBookingLoading }] = useMutation(ADD_NOTE_TO_BOOKING);
  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);
  const [revertChange] = useMutation(REVERT_CHANGE);

  const isGroup = booking.isGroupBooking;
  const usesGroupDetailsForm = hasGroupDetailsForm(booking.productFamilyGroup);
  const showDOB = booking.isDOBRequired;

  const meals = useMeals({
    booking: booking
  });

  const defaultLanguage = booking?.additionalDetails?.find(({ key }) => key === LANGUAGE_KEY)?.value || DEFAULT_LANGUAGE;
  const [bookingLanguage, setBookingLanguage] = useState(defaultLanguage);
  const [businessBenefitError, setBusinessBenefitError] = useState(null);

  const outbound = booking.outbound?.filter(({ activeBookingItems }) =>
    activeBookingItems.some(({ cancelled, provisional }) => !cancelled && ((addPassengers && !provisional) || provisional))
  );

  const nonCancelledCheckedInOutboundBookingItems = flatten(
    nonCancelledLegs(booking.outbound)
      ?.filter(
        ({ activeBookingItems, serviceType }) =>
          activeBookingItems.some(({ cancelled, isCheckedIn, canChangeName }) => !cancelled && (isCheckedIn || !canChangeName)) ||
          serviceType.code === 'SN'
      )
      .map(({ activeBookingItems }) => activeBookingItems)
  );

  const inbound = booking.inbound?.filter(({ activeBookingItems }) =>
    activeBookingItems.some(({ cancelled, provisional }) => !cancelled && ((addPassengers && !provisional) || provisional))
  );

  const nonCancelledCheckedInInboundBookingItems = flatten(
    nonCancelledLegs(booking.inbound)
      ?.filter(({ activeBookingItems }) =>
        activeBookingItems.some(({ cancelled, isCheckedIn, canChangeName }) => !cancelled && (isCheckedIn || !canChangeName))
      )
      .map(({ activeBookingItems }) => activeBookingItems)
  );

  const outboundPassengersAllCarriers = flatten(
    outbound.map(({ activeBookingItems }) =>
      activeBookingItems.map(({ passenger }) => ({
        ...passenger,
        phoneNumber: passenger.phoneNumber ? passenger.phoneNumber.replace(/[^\d.]/g, '') : null
      }))
    )
  );
  const inboundPassengersAllCarriers = flatten(
    inbound.map(({ activeBookingItems }) =>
      activeBookingItems.map(({ passenger }) => ({
        ...passenger,
        phoneNumber: passenger.phoneNumber ? passenger.phoneNumber.replace(/[^\d.]/g, '') : null
      }))
    )
  );
  const outboundPassengers = outboundPassengersAllCarriers?.reduce((acc, val) => {
    if (!acc.find((item) => item.id === val.id)) {
      acc.push({
        ...val,
        isPhoneNumberAndEmailDisabled:
          nonCancelledCheckedInOutboundBookingItems.some(({ passengerId }) => passengerId === val.id) ||
          nonCancelledCheckedInInboundBookingItems.some(({ passengerId }) => passengerId === val.id)
      });
    }
    return acc;
  }, []);
  const inboundPassengers = inboundPassengersAllCarriers?.reduce((acc, val) => {
    if (!acc.find((item) => item.id === val.id)) {
      acc.push({
        ...val,
        isPhoneNumberAndEmailDisabled:
          nonCancelledCheckedInOutboundBookingItems.some(({ passengerId }) => passengerId === val.id) ||
          nonCancelledCheckedInInboundBookingItems.some(({ passengerId }) => passengerId === val.id)
      });
    }
    return acc;
  }, []);

  let passengers = [];
  let existingPassengers = [];

  if (outboundPassengers.length > 0) {
    existingPassengers = outboundPassengers;
  }

  if (inboundPassengers.length > 0) {
    existingPassengers = inboundPassengers;
  }

  let legId;
  let legIds;

  if (addPassengers) {
    passengers = passengers.concat(
      booking.pendingCreationLegs
        .find(
          ({ direction, classOfService }) =>
            (outboundClass && direction === DIRECTIONS.OUTBOUND && classOfService.code === outboundClass) ||
            (inboundClass && direction === DIRECTIONS.INBOUND && classOfService.code === inboundClass)
        )
        .activeBookingItems.map((bi) => bi.passenger)
    );

    existingPassengers = existingPassengers.map((p) => ({
      ...p,
      isReadOnly: true
    }));

    legId = [...outbound, ...inbound].find(
      ({ classOfService, activeBookingItems }) =>
        activeBookingItems.some(({ provisional }) => Boolean(provisional)) &&
        (inboundClass === classOfService.code || outboundClass === classOfService.code)
    )?.id;
    legIds = [...outbound, ...inbound]
      .filter(
        ({ activeBookingItems, classOfService, direction }) =>
          activeBookingItems.some(({ provisional }) => Boolean(provisional)) &&
          ((outboundClass && direction === DIRECTIONS.OUTBOUND && classOfService.code === outboundClass) ||
            (inboundClass && direction === DIRECTIONS.INBOUND && classOfService.code === inboundClass))
      )
      .map((leg) => leg.id);
  }

  passengers = passengers.concat(existingPassengers.filter((ep) => !passengers.find((p) => p.id === ep.id)));

  const passengerDetails = formatPassengers(passengers);

  const defaultValues = {
    passengers
  };

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues
  });
  const { register, control, formState, watch, setValue, getValues, setError } = formMethods;
  const { errors } = formState;

  const { fields } = useFieldArray({ control, name: 'passengers' });
  const onSubmitLoyaltyNumber = async (index, loyaltyNumber) => {
    try {
      const { data, errors: submitLoyaltyNumber } = await apolloClient.query({
        query: GET_MEMBERSHIP_DETAILS,
        variables: {
          membershipNumber: `${LOYALTY_NUMBER_PREFIX}${loyaltyNumber}`
        }
      });
      const customerInfo = data?.getCustomerDetails;

      if (customerInfo && !submitLoyaltyNumber?.length) {
        if (customerInfo?.firstName) {
          setValue(`passengers.${index}.firstName`, customerInfo.firstName, {
            shouldValidate: true
          });
        }
        if (customerInfo?.lastName) {
          setValue(`passengers.${index}.lastName`, customerInfo.lastName, {
            shouldValidate: true
          });
        }
        if (customerInfo?.emailAddress) {
          setValue(`passengers.${index}.email`, customerInfo.emailAddress, {
            shouldValidate: true
          });
        }
        if (customerInfo?.profile?.dateOfBirth) {
          setValue(`passengers.${index}.birthDate`, customerInfo.profile.dateOfBirth, {
            shouldValidate: true
          });
        }
        if (customerInfo?.phoneNumber) {
          const { countryCode, number } = parsePhoneNumber(customerInfo.phoneNumber);

          if (countryCode) {
            setValue(`passengers.${index}.phoneCountryCode`, countryCode.replace('+', ''), {
              shouldValidate: true
            });
          }

          if (number) {
            setValue(`passengers.${index}.phoneNumber`, number, {
              shouldValidate: true
            });
          }
        }
      } else {
        setError(`passengers.${index}.loyaltyNumber`, 'validate', 'paxForm.validator.loyaltyAccountNotFound');
      }
    } catch (e) {
      setError(`passengers.${index}.loyaltyNumber`, 'validate', 'paxForm.validator.loyaltyAccountNotFound');
    }
  };

  const hasError = ({ name, index }) => Boolean(errors && errors.passengers && errors.passengers[index] && errors.passengers[index][name]);

  const getError = ({ name, index }) => errors?.passengers?.[index]?.[name]?.message || '';

  const isNewPassenger = (id) => booking.pendingCreationLegs[0].activeBookingItems.map((bi) => bi.passenger.id).includes(id);

  const copyLastNameToAllPassengers = () => {
    const { passengers: passengersFromState } = getValues();
    const firstPassenger = passengersFromState[0];
    const firstPassengerLastName = firstPassenger && firstPassenger.lastName;
    passengersFromState.forEach((_, i) => {
      if (addPassengers && isNewPassenger(passengers[i].id)) {
        setValue(`passengers.${i}.lastName`, firstPassengerLastName, {
          shouldValidate: true
        });
      }
    });
  };

  const isContactFieldEditable = (passenger, field) => {
    const isReadOnly = !addPassengers || passenger.isReadOnly;

    return !passenger.isPhoneNumberAndEmailDisabled && !(isReadOnly && passenger[field]);
  };

  const copyEmailAllPassengers = () => {
    const { passengers: passengersFromState } = getValues();
    const firstPassenger = passengersFromState[0];
    const firstPassengerEmail = firstPassenger && firstPassenger.email;
    passengersFromState.forEach((_, i) => {
      if (isContactFieldEditable(passengerDetails[i], 'email')) {
        setValue(`passengers.${i}.email`, firstPassengerEmail, {
          shouldValidate: true
        });
      }
    });
  };

  const copyPhoneNumberAllPassengers = () => {
    const { passengers: passengersFromState } = getValues();
    const firstPassenger = passengersFromState[0];
    const firstPassengerPhoneNumber = firstPassenger && firstPassenger.phoneNumber;
    const firstPassengerPhoneCountryCode = firstPassenger && firstPassenger.phoneCountryCode;
    passengersFromState.forEach((_, i) => {
      if (isContactFieldEditable(passengerDetails[i], 'phoneNumber')) {
        setValue(`passengers.${i}.phoneCountryCode`, firstPassengerPhoneCountryCode, {
          shouldValidate: true
        });
        setValue(`passengers.${i}.phoneNumber`, firstPassengerPhoneNumber, {
          shouldValidate: true
        });
      }
    });
  };

  const getBusinessBenefitUpgradeContext = () => {
    const pendingCreationLeg = booking?.pendingCreationLegs?.find((leg) =>
      leg.products?.some((product) => product.provisional && !product.cancelled)
    );

    if (pendingCreationLeg) {
      const productWithTicketNumber = pendingCreationLeg.products?.find(
        (product) => product.provisional && !product.cancelled && product.ticketNumber
      );
      const product = productWithTicketNumber || pendingCreationLeg.products?.find((product) => product.provisional && !product.cancelled);

      return product ? { leg: pendingCreationLeg, product } : null;
    }

    const allLegs = [...(booking?.outbound ?? []), ...(booking?.inbound ?? [])];
    const fallbackLeg = allLegs.find((leg) => leg.activeBookingItems?.some((item) => item.provisional && !item.cancelled));

    if (!fallbackLeg) {
      return null;
    }

    const productWithTicketNumber = fallbackLeg.activeBookingItems?.find(
      (item) => item.provisional && !item.cancelled && item.ticketNumber
    );
    const product = productWithTicketNumber || fallbackLeg.activeBookingItems?.find((item) => item.provisional && !item.cancelled);

    return product ? { leg: fallbackLeg, product } : null;
  };

  const handleBusinessBenefitUpgrade = async () => {
    if (!businessId || !businessBenefitName || !membershipNumber) {
      setBusinessBenefitError(new Error('Missing business benefit upgrade details.'));
      return false;
    }

    const upgradeContext = getBusinessBenefitUpgradeContext();

    if (!upgradeContext?.product?.itemRef) {
      setBusinessBenefitError(new Error('No upgraded ticket found for business benefit upgrade.'));
      return false;
    }

    const { leg, product } = upgradeContext;
    const itemRefId = Number(product.itemRef);

    if (!itemRefId) {
      setBusinessBenefitError(new Error('No upgraded ticket reference found for business benefit upgrade.'));
      return false;
    }

    const carrier = leg?.serviceType?.code ?? null;
    const origin = leg?.origin?.uic ?? null;
    const destination = leg?.destination?.uic ?? null;
    const departureDateTime = leg?.timing?.isoDate ?? null;

    try {
      const redeemResult = await redeemBusinessAccountBenefit({
        variables: {
          businessId,
          membershipNumber,
          benefitName: businessBenefitName,
          bookingReference: booking.reference,
          carrier,
          origin,
          destination,
          departureDateTime
        }
      });

      const redeemSuccess = redeemResult?.data?.redeemBusinessAccountBenefit?.success;
      if (!redeemSuccess) {
        throw new Error('Business benefit redemption failed.');
      }
    } catch (error) {
      setBusinessBenefitError(error);
      return false;
    }

    try {
      await confirmBooking({
        variables: {
          bookingReference: booking.reference
        }
      });
    } catch (error) {
      try {
        await revertChange({
          variables: {
            bookingReference: booking.reference
          }
        });
      } catch (revertError) {
        setBusinessBenefitError(revertError);
        return false;
      }

      setBusinessBenefitError(error);
      return false;
    }

    const ticketNumber = product?.ticketNumber ?? '';

    try {
      await addNoteToBooking({
        variables: {
          bookingReference: booking.reference,
          note: `Business benefit upgrade (${businessBenefitName})
	  for ticket ${ticketNumber},
	  business ${businessId}, Club Eurostar ${membershipNumber}`
        }
      });

      const timestamp = new Date().toISOString();

      await addAdditionalDetails({
        variables: {
          bookingReference: booking.reference,
          additionalDetails: [
            {
              itemRefId,
              key: 'free_upgrade_timestamp',
              value: timestamp
            }
          ]
        }
      });

      await addAdditionalDetails({
        variables: {
          bookingReference: booking.reference,
          additionalDetails: [
            {
              itemRefId,
              key: 'business_benefit_name',
              value: businessBenefitName
            }
          ]
        }
      });
    } catch (error) {
      setBusinessBenefitError(error);
      return false;
    }

    return true;
  };

  const handleSubmit =
    (updatePassengerSeats = () => null, passengersList = []) =>
    async (event) => {
      event.preventDefault();

      let nextRoute = '';

      if (addPassengers) {
        try {
          const passengerValues = getValues()?.passengers || watch('passengers');
          const newPassengerDetails = passengerValues
            .map((newValues, index) => ({
              ...passengersList[index],
              ...newValues
            }))
            .filter((passenger, index) => {
              const isOnHoldGroupBooking = isGroup && booking.status === bookingStatus.TEMPORARILY_ON_HOLD;
              const isNewPassenger = booking.pendingCreationLegs[0].activeBookingItems
                .map((bookingItem) => bookingItem.passenger.id)
                .includes(passenger.id);
              const isPhoneNumberChange = passenger.phoneNumber && passenger.phoneNumber !== passengersList[index].phoneNumber;
              return isOnHoldGroupBooking || isNewPassenger || isPhoneNumberChange;
            })
            .map(renameKeys({ id: 'paxId' }));

          const loyaltyChecks = await Promise.all(
            newPassengerDetails.map(async (passenger, index) => {
              try {
                if (passenger.loyaltyNumber) {
                  let hasErrorB = false;

                  const passengerLoyaltyNumber =
                    passenger.loyaltyNumber && `${passenger.loyaltyNumber}`.startsWith(`${LOYALTY_NUMBER_PREFIX}`)
                      ? `${passenger.loyaltyNumber}`.replace(`${LOYALTY_NUMBER_PREFIX}`, '')
                      : passenger.loyaltyNumber;
                  const { data } = await apolloClient.query({
                    query: GET_MEMBERSHIP_DETAILS,
                    variables: {
                      membershipNumber: `${LOYALTY_NUMBER_PREFIX}${passengerLoyaltyNumber}`
                    }
                  });
                  const customerInfo = data?.getCustomerDetails;

                  if (passenger?.firstName?.toUpperCase() !== customerInfo?.firstName?.toUpperCase()) {
                    setError(`passengers.${index}.firstName`, 'validate', 'paxForm.validator.incorrectFirstName');

                    hasErrorB = true;
                  }

                  if (passenger?.lastName?.toUpperCase() !== customerInfo?.lastName?.toUpperCase()) {
                    setError(`passengers.${index}.lastName`, 'validate', 'paxForm.validator.incorrectLastName');

                    hasErrorB = true;
                  }

                  return !hasErrorB;
                }

                return true;
              } catch {
                setError(`passengers.${index}.loyaltyNumber`, 'validate', 'paxForm.validator.notExistLoyaltyNumber');

                return false;
              }
            })
          );

          if (loyaltyChecks.some((value) => value === false)) {
            return null;
          }

          await updatePassengerSeats();

          const discountCards = [];

          if (discountCardsMap[booking.productFamilyGroup]) {
            discountCards.push(discountCardsMap[booking.productFamilyGroup]);
          }

          let groupName;
          let groupEmail;
          let groupContactNumber;

          if (usesGroupDetailsForm) {
            groupName = pathOr('', ['companyName'], booking.customer);
            groupEmail = pathOr('', ['email'], booking.customer);
            groupContactNumber = `${booking.customer?.unformattedPhoneNumber?.countryCode?.replace('+', '') ?? ''}${
              booking.customer?.unformattedPhoneNumber?.number ?? ''
            }`;
          }

          await updatePassengers({
            variables: {
              bookingReference: booking.reference,
              passengers: newPassengerDetails.map(
                ({ firstName, lastName, birthDate, email, phoneCountryCode, phoneNumber, loyaltyNumber, paxId }) => ({
                  id: paxId,
                  firstName: usesGroupDetailsForm ? groupName.trim() : firstName.trim(),
                  lastName: usesGroupDetailsForm ? groupName.trim() : lastName.trim(),
                  email: usesGroupDetailsForm ? groupEmail : email,
                  discountCards: getLoyaltyData(loyaltyNumber, paxId, passengersList).concat(discountCards),
                  ...(birthDate && { birthDate: correctlyFormatDate(birthDate) }),
                  phoneNumber: usesGroupDetailsForm
                    ? groupContactNumber
                    : phoneCountryCode && phoneNumber && `${phoneCountryCode}${phoneNumber.replace(/^0+/, '')}`
                })
              )
            }
          });

          const hasAnimalAssistance = newPassengerDetails.some(({ type }) => type === types.DOG);

          const params = new URLSearchParams();

          params.append('currency', currency);

          if (isFreeUpgrade) {
            params.append('isFreeUpgrade', isFreeUpgrade);
          }

          if (membershipNumber) {
            params.append('membershipNumber', membershipNumber);
          }

          if (addPassengers) {
            params.append('addPassengers', addPassengers);
          }

          if (waivePoints) {
            params.append('waivePoints', waivePoints);
          }

          if (addPassengers && hasAnimalAssistance) {
            params.append('newPassengersIds', newPassengerDetails.map(({ paxId }) => paxId).join(''));

            nextRoute = `/exchange/${booking.reference}/pax/assistance?${params.toString()}`;
          } else if (isGroup && booking.status === bookingStatus.TEMPORARILY_ON_HOLD) {
            nextRoute = booking.changeExpiryTimestamp
              ? `/exchange/${booking.reference}/payment?${params.toString()}`
              : `/manage/bookings/${booking.reference}/journeys`;
          } else {
            nextRoute = `/exchange/${booking.reference}/confirm?${params.toString()}`;
          }
        } catch (error) {
          return error;
        }
      } else {
        await updatePassengerSeats();

        const passengerFormList = getValues()?.passengers || passengersList;

        const paxs = passengersList
          ?.map((pax, index) => ({
            id: pax.id,
            ...(passengerFormList[index]?.email && passengersList[index]?.email !== passengerFormList[index]?.email
              ? { email: passengerFormList[index].email }
              : {}),
            ...(passengerFormList[index]?.phoneNumber && passengersList[index]?.phoneNumber !== passengerFormList[index]?.phoneNumber
              ? { phoneNumber: `${passengerFormList[index].phoneCountryCode}${passengerFormList[index].phoneNumber.replace(/^0+/, '')}` }
              : {}),
            ...(passengerFormList[index]?.birthDate && passengersList[index]?.birthDate !== passengerFormList[index]?.birthDate
              ? { birthDate: passengerFormList[index].birthDate }
              : {})
          }))
          .filter((pax) => Object.keys(pax).length > 1);

        if (paxs.length > 0) {
          await updatePassengers({
            variables: {
              bookingReference: booking.reference,
              passengers: paxs
            }
          });
        }

        const params = new URLSearchParams();

        if (membershipNumber) {
          params.set('membershipNumber', membershipNumber);
        }

        if (isTierBenefitUpgrade || isFreeUpgrade || isBusinessBenefitUpgrade) {
          nextRoute = `/manage/bookings/${booking.reference}/journeys?${params.toString()}`;
        } else {
          if (currency) {
            params.set('currency', currency);
          }

          nextRoute = `/exchange/${booking.reference}/confirm?${params.toString()}`;
        }
      }

      await meals.saveMeals();
      await updateBookingLanguage(apolloClient, booking?.additionalDetails, booking?.reference, bookingLanguage);

      if (isTierBenefitUpgrade) {
        await confirmTierBenefitUpgrade({
          variables: {
            bookingReference: booking.reference,
            membershipNumber: membershipNumber
          }
        });
      }

      if (isFreeUpgrade) {
        await confirmFreeUpgrade({
          variables: {
            bookingReference: booking.reference,
            currency,
            ticketNumbers: [
              ...outbound.flatMap(({ activeBookingItems }) =>
                activeBookingItems.reduce((acc, { ticketNumber, provisional }) => {
                  if (provisional) {
                    acc.push(ticketNumber);
                  }

                  return acc;
                }, [])
              ),
              ...inbound.flatMap(({ activeBookingItems }) =>
                activeBookingItems.reduce((acc, { ticketNumber, provisional }) => {
                  if (provisional) {
                    acc.push(ticketNumber);
                  }

                  return acc;
                }, [])
              )
            ]
          }
        });
      }

      if (isBusinessBenefitUpgrade) {
        const businessUpgradeSucceeded = await handleBusinessBenefitUpgrade();
        if (!businessUpgradeSucceeded) {
          return;
        }
      }

      history.push(nextRoute);
    };

  const [isValid, setIsValid] = useState(formState.isValid);
  useEffect(() => {
    if (formState.isValid !== isValid) {
      setIsValid(formState.isValid);
    }
  }, [formState.isValid, isValid, setIsValid]);

  const [isSubmitting, setIsSubmitting] = useState(formState.isSubmitting);
  useEffect(() => {
    if (formState.isSubmitting !== isSubmitting) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState.isSubmitting, isSubmitting, setIsSubmitting]);

  if (businessBenefitError) {
    throw new AgentPortalError(businessBenefitError);
  }

  if (outbound.length === 0 && inbound.length === 0) {
    return (
      <Row columns={32}>
        <Column start={2} span={32}>
          <BackButton
            element={
              <Link
                to={`/manage/bookings/${booking.reference}/journeys${membershipNumber ? `?membershipNumber=${membershipNumber}` : ''}`}
              />
            }
          >
            <FormattedMessage id="exchanges.back" />
          </BackButton>
        </Column>
        <Column start={2} span={32}>
          <h1>
            <FormattedMessage id="exchanges.noExchanges" />
          </h1>
        </Column>
      </Row>
    );
  }

  return (
    <SeatSelection
      bookingData={booking}
      outbound={nonCancelledEurostarAndGreenspeedLegs(outbound)}
      inbound={nonCancelledEurostarAndGreenspeedLegs(inbound)}
      legId={legId}
      visibleLegIds={legIds}
      isExchange
      isNewBooking
    >
      {({
        legId,
        renderedSeatSelection,
        isUpdatingSeats,
        selectedSeats = [],
        selectedPassenger,
        selectedPassengers,
        disabledContinue,
        journey,
        actions: { updatePassengerSeats, onSelectNewPassenger, onRemovePassengerSelection }
      }) => {
        const isConfirmButtonDisabled = !isValid || isSubmitting || isUpdatingSeats || disabledContinue;

        return (
          <FormProvider {...formMethods}>
            <Page>
              {renderedSeatSelection ? (
                <Row span={14} start={2}>
                  <Row span={16}>{renderedSeatSelection}</Row>
                </Row>
              ) : null}
              {usesGroupDetailsForm ? (
                <>
                  {!meals.isDone ? (
                    <Spinner />
                  ) : (
                    <GroupDetails
                      isDisabled={true}
                      bookingData={booking}
                      {...{
                        selectedSeats,
                        selectedPassenger,
                        selectedPassengers,
                        updatePassengerSeats,
                        onSelectNewPassenger,
                        onRemovePassengerSelection
                      }}
                      bookingLanguage={bookingLanguage}
                      setBookingLanguage={setBookingLanguage}
                    />
                  )}
                </>
              ) : (
                <form>
                  <PassengerDetailsForm
                    isDisabled={!addPassengers}
                    selectedSeats={selectedSeats}
                    selectedPassenger={selectedPassenger}
                    selectedPassengers={selectedPassengers}
                    onSelectNewPassenger={onSelectNewPassenger}
                    onRemovePassengerSelection={onRemovePassengerSelection}
                    passengerDetails={passengerDetails}
                    showDOB={showDOB}
                    fields={fields}
                    register={register}
                    hasError={hasError}
                    getError={getError}
                    getValues={getValues}
                    setValue={setValue}
                    direction={journey?.direction}
                    exchangePath={history?.location?.pathname?.includes('/exchange')}
                    onSubmitLoyaltyNumber={onSubmitLoyaltyNumber}
                    copyLastNameToAllPassengers={copyLastNameToAllPassengers}
                    copyEmailAllPassengers={copyEmailAllPassengers}
                    copyPhoneNumberAllPassengers={copyPhoneNumberAllPassengers}
                    bookingLanguage={bookingLanguage}
                    setBookingLanguage={setBookingLanguage}
                    meals={meals.availableMeals[legId]}
                    mealsToUpdate={meals.mealsToUpdate}
                    setMealsForPassenger={meals.setMealsForPassenger}
                    hasNoMealRoute={isNoMealRoute(journey?.origin?.uic, journey?.destination?.uic)}
                  />
                </form>
              )}
              <ButtonRow span={10}>
                <ConfirmButton
                  data-cy="exchanges-change-seat-continue"
                  type="submit"
                  loading={
                    isUpdatingSeats ||
                    updatePassengersLoading ||
                    confirmTierBenefitUpgradeLoading ||
                    confirmFreeUpgradeLoading ||
                    redeemBusinessBenefitLoading ||
                    confirmBookingLoading ||
                    addNoteToBookingLoading ||
                    addAdditionalDetailsLoading ||
                    !meals.isDone
                  }
                  disabled={
                    isConfirmButtonDisabled ||
                    updatePassengersLoading ||
                    confirmTierBenefitUpgradeLoading ||
                    confirmFreeUpgradeLoading ||
                    redeemBusinessBenefitLoading ||
                    confirmBookingLoading ||
                    addNoteToBookingLoading ||
                    addAdditionalDetailsLoading
                  }
                  onClick={handleSubmit(updatePassengerSeats, passengers)}
                  variant="primary"
                >
                  {isTierBenefitUpgrade || isFreeUpgrade || isBusinessBenefitUpgrade ? (
                    <FormattedMessage id="exchanges.confirmUpgrade" />
                  ) : (
                    <FormattedMessage id="exchanges.continue" />
                  )}
                </ConfirmButton>
                <RevertExchangeButton
                  reference={booking.reference}
                  currency={currency}
                  payments={booking.payments}
                  hasHotel={booking.isPackage || hasHotel(booking)}
                  status={booking.status}
                  changeExpiryTimestamp={booking.changeExpiryTimestamp}
                />
              </ButtonRow>
            </Page>
          </FormProvider>
        );
      }}
    </SeatSelection>
  );
};

ExchangeSeatSelection.propTypes = {
  booking: bookingShape.isRequired,
  history: historyShape.isRequired,
  user: userShape.isRequired
};

export default ExchangeSeatSelection;
